import {
  getLeoUuid,
  LeoUUID,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyQuestionViewDetails, SurveyQuestionViewDetailsDTO } from "./surveyQuestionViewDetails";
/* eslint-disable import/export */
export abstract class GetSurveyQuestionViewsRPC implements
  LeoRPC<GetSurveyQuestionViewsRPC.Request, GetSurveyQuestionViewsRPC.Response, GetSurveyQuestionViewsRPC.Errors.Errors>
{
  abstract execute(
    request: GetSurveyQuestionViewsRPC.Request
  ): Promise<LeoRPCResult<GetSurveyQuestionViewsRPC.Response, GetSurveyQuestionViewsRPC.Errors.Errors>>;
}

export namespace GetSurveyQuestionViewsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    surveyViewId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetSurveyQuestionViewsRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly surveyViewId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID, 
    surveyViewId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.surveyId = surveyId;
      this.surveyViewId = surveyViewId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const surveyViewId = getLeoUuid(dto, "surveyViewId", InvalidRequestSurveyViewIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        surveyId,
        surveyViewId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          surveyViewId: this.surveyViewId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetSurveyQuestionViewsRPC(surveyId='${this.surveyId}',surveyViewId='${this.surveyViewId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyQuestionViews: SurveyQuestionViewDetailsDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetSurveyQuestionViewsResponse: boolean = true;
  
    readonly surveyQuestionViews: SurveyQuestionViewDetails[];
  
  
    constructor(
    surveyQuestionViews: SurveyQuestionViewDetails[]
    ) {
      this.surveyQuestionViews = surveyQuestionViews;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyQuestionViews = getList<SurveyQuestionViewDetailsDTO, SurveyQuestionViewDetails>(
        dto,
        "surveyQuestionViews",
        SurveyQuestionViewDetails.fromDTO,
        InvalidResponseSurveyQuestionViewsError
      );
      return new Response(
        surveyQuestionViews
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyQuestionViews: this.surveyQuestionViews.map((e: SurveyQuestionViewDetails) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetSurveyQuestionViews(surveyQuestionViews='${this.surveyQuestionViews}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidSurveyViewId = "INVALID_SURVEY_VIEW_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetSurveyQuestionViewsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetSurveyQuestionViewsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidSurveyViewIdDTO {
      code: string;
    }
    
    export class InvalidSurveyViewId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyViewId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyViewId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyViewIdDTO {
        const dto: InvalidSurveyViewIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyViewId.prototype.toString = function toString(): string {
      return `GetSurveyQuestionViewsRPC.INVALID_SURVEY_VIEW_ID()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | InvalidSurveyViewId;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | InvalidSurveyViewIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidSurveyViewId:
          return new InvalidSurveyViewId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends GetSurveyQuestionViewsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestSurveyViewIdError extends GetSurveyQuestionViewsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyViewIdError";
  }
}

export class InvalidRequestProjectIdError extends GetSurveyQuestionViewsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseSurveyQuestionViewsError extends GetSurveyQuestionViewsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyQuestionViewsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetSurveyQuestionViewsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetSurveyQuestionViewsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidSurveyViewIdError extends GetSurveyQuestionViewsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyViewIdError";
  }
}

